import { IconMessageChatbot } from "@exopengithub/exokit";
import clsx from "clsx";
import type { ReactNode } from "react";
import { NavLink } from "react-router";

interface ConversationListItemProps {
	title: string;
	ariaLabel?: string;
	/** Bolds the title — used for search results whose title matched the query. */
	highlightTitle?: boolean;
	/** Right-aligned content next to the title, e.g. a relative date or an action icon. */
	trailing?: ReactNode;
	/** Extra content below the title row, e.g. matched-message snippets. */
	children?: ReactNode;
	onClick?: () => void;
	/** Renders as a route-aware NavLink instead of firing onClick — used by the always-visible sidebar list. */
	to?: string;
}

const rowClassName = (isActive: boolean) =>
	clsx(
		"group flex w-full flex-col gap-1 border-b border-grey-200 px-6 py-3 text-left last:border-b-0",
		isActive ? "bg-grey-50" : "hover:bg-grey-50",
	);

// Shared row for every "pick a conversation" list, so icon/hover styling can't drift between them.
export const ConversationListItem = ({
	title,
	ariaLabel,
	highlightTitle = false,
	trailing,
	children,
	onClick,
	to,
}: ConversationListItemProps) => {
	const content = (
		<>
			<span className="flex items-center justify-between gap-4">
				<span className="flex min-w-0 items-center gap-2">
					<IconMessageChatbot size={16} className="shrink-0 text-grey-300" />
					<span
						className={clsx(
							"min-w-0 truncate text-sm text-gray-700",
							highlightTitle && "font-bold",
						)}
					>
						{title}
					</span>
				</span>
				{trailing}
			</span>
			{children}
		</>
	);

	if (to) {
		return (
			<NavLink
				to={to}
				aria-label={ariaLabel}
				className={({ isActive }) => rowClassName(isActive)}
			>
				{content}
			</NavLink>
		);
	}

	return (
		<button
			type="button"
			onClick={onClick}
			aria-label={ariaLabel}
			className={rowClassName(false)}
		>
			{content}
		</button>
	);
};
